import useSWR from "swr";
import { experimentsApi, chatRoomsApi } from "@/lib/api";
import { useAuth } from "@/lib/config/auth-provider";
import { isTrialTier, TRIAL_MAX_CHAT_ROOMS, TRIAL_MAX_STUDIES } from "@/lib/billing/trial-limits";

/**
 * Whether the current account is on the no-card `trial` tier that carries the
 * operational caps (5 messages/room, 3 rooms, 1 study). Reads the profile
 * snapshot already loaded by AuthProvider — no extra request.
 */
export function useTrialStatus(): { isTrial: boolean } {
  const { user } = useAuth();
  return { isTrial: isTrialTier(user?.subscriptionTier) };
}

/**
 * Resolves whether a trial user has already used all their allowed chat rooms.
 *
 * Only fetches the room count when the user is on the capped `trial` tier, so
 * paid/unlimited tiers incur no extra request. The count mirrors the backend's
 * `countByUserToken` check that guards room creation.
 */
export function useTrialChatRoomLimit(): {
  isTrial: boolean;
  isLoading: boolean;
  roomCount: number;
  limitReached: boolean;
} {
  const { isTrial } = useTrialStatus();

  const { data, isLoading } = useSWR(
    isTrial ? "trial-room-count" : null,
    () => chatRoomsApi.getConversationHistory({ limit: 1 }),
    { revalidateOnFocus: false },
  );

  const roomCount = data?.total ?? 0;

  return {
    isTrial,
    isLoading: isTrial && isLoading,
    roomCount,
    limitReached: isTrial && roomCount >= TRIAL_MAX_CHAT_ROOMS,
  };
}

/**
 * Resolves whether a trial user has already used their single study allowance.
 *
 * Only fetches the study count when the user is on the capped `trial` tier, so
 * paid/unlimited tiers incur no extra request. The count mirrors the backend's
 * `countByUserToken` check that guards study creation.
 */
export function useTrialStudyLimit(): {
  isTrial: boolean;
  isLoading: boolean;
  studyCount: number;
  limitReached: boolean;
} {
  const { isTrial } = useTrialStatus();

  const { data, isLoading } = useSWR(
    isTrial ? "trial-study-count" : null,
    () => experimentsApi.listPaginated(1, 1),
    { revalidateOnFocus: false },
  );

  const studyCount = data?.total ?? 0;

  return {
    isTrial,
    isLoading: isTrial && isLoading,
    studyCount,
    limitReached: isTrial && studyCount >= TRIAL_MAX_STUDIES,
  };
}
